import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ClientEnvironmentModel } from 'src/app/models/client-environment';
import { HLConversationAgent, HLConversationModel } from 'src/app/models/conversations';
import { BotModel } from 'src/app/models/bot';
import { HierarchyElementModel } from 'src/app/models';
import { AuthService } from 'src/app/services/auth.service';
import { DumbledoreService } from 'src/app/services/dumbledore.service';
import { isDigitsOnly, formatDigitsOnlyToPhoneNumber } from 'src/app/utils/strings/string.utils';

const CONVERSATION_TYPE_LABELS: Readonly<Record<string, string>> = {
  AC: 'Abandoned Customer',
  DS: 'Declined Services',
  FS: 'First Service',
  FU: 'Followup',
  NS: 'Next Service',
  PS: 'Pit Stop',
  PC: 'Pre CSI',
  PF: 'Prepaid First Service',
  PN: 'Prepaid Next Service',
  MI: 'Missed Interval',
  SF: 'SpiFi',
  SL: 'Sales Lead',
  LS: 'Sales Lead With Source',
  SI: 'State Inspection',
  EW: 'Extended Warranty',
  RC: 'Recall',
  PMA: 'Product Marketing',
  NADA: 'NADA',
  FO: 'First Service Overdue',
  SS: 'Second Service',
  SO: 'Second Service Overdue',
  L4: 'Lapsed Service 4 Mos Overdue',
  L6: 'Lapsed Service 6 Mos Overdue',
  L7: 'Lapsed Service 7 Mos Overdue',
  AR: 'Appointment Reminder',
  MR: 'Missed Appointment Reminder',
  TI: 'Tires Due',
  TO: 'Tires Overdue',
  TPS: 'Service Intro After Purchase',
  TT1: 'ToyotaCare Due - 5k',
  TT2: 'ToyotaCare Due - 10k',
  TT3: 'ToyotaCare Due - 15k',
  TT4: 'ToyotaCare Due - 20k',
  TT5: 'ToyotaCare Due - 25k',
  TT6: 'ToyotaCare Due',
  BTD: 'Service Due After ToyotaCare',
  TNR: 'ToyotaCare Non-Responder',
  TCP: 'Service Thank You',
  TL4: 'Lapsed Service - 4 Mos Overdue',
  TL6: 'Lapsed Service - 6 Mos Overdue',
  TL7: 'TLE At-Risk (7-9 Months)',
  TAC: 'TLE Inactive (13-23 Months)',
  TDS: 'Additional Services Recommended',
  TAR: 'Appointment Reminder',
  TTD: 'Tires Due',
  TTO: 'Tires Overdue',
  PO: 'Payment Opportunity',
  CB: 'Service Lane Car Buying',
};

@Component({
  selector: 'app-hl-meta-data-list',
  templateUrl: './hl-meta-data-list.component.html',
  styleUrls: ['./hl-meta-data-list.component.scss'],
})
export class HlMetaDataListComponent implements OnInit, OnChanges {
  @Input() selectedConversation: HLConversationModel;

  @Input() corpId: string;

  @Input() envs: ClientEnvironmentModel;
  @Input() bot: BotModel | null;
  botUrl: string;
  loading: boolean;
  private loadedConversationId: string | undefined;
  assignments: HLConversationAgent[];
  showServiceReps = false;
  location: string | undefined;
  campaignType: string | undefined;
  vehicle: any;
  hideVehicleDetails = false;
  internalUser = false;
  firstCustomerEmail: string | null = null;
  firstCustomerPhone: string | null = null;

  @Input() hierarchyElement: HierarchyElementModel | null;
  constructor(private dumbledoreService: DumbledoreService, private authService: AuthService) {}

  ngOnInit() {
    this.extractCustomerContacts();
    this.authService.currentUser.then(currentUser => {
      if (currentUser && currentUser.email) {
        this.internalUser = currentUser.email.toLowerCase().indexOf('@carlabs.') > 0 || currentUser.email.toLowerCase().indexOf('@impel.') > 0;
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedConversation) {
      this.extractCustomerContacts();
      this.assignments = this.selectedConversation.assignments ? this.selectedConversation.assignments : [];
      this.location = this.hierarchyElement?.label;
      this.campaignType = this.selectedConversation.campaign ? this.getConversationType(this.selectedConversation.campaign) : undefined;
      const conversationId = this.selectedConversation.firebaseDocumentId;
      if (conversationId && conversationId === this.loadedConversationId) {
        // Live update of the conversation already on screen: refresh in place instead of blanking the card.
        this.updateBotUrl();
        return;
      }
      this.loadedConversationId = conversationId;
      this.loading = true;
      const vehicle_result = conversationId ? await this.dumbledoreService.getVehicleByLegacyConversationId(conversationId) : undefined;
      if (this.loadedConversationId !== conversationId) {
        return;
      }
      this.vehicle = vehicle_result?.user;
      this.hideVehicleDetails = !!this.vehicle?.vin?.startsWith('PO-');
      this.refreshContent();
    }
  }

  private getConversationType(conversationType: string): string {
    return CONVERSATION_TYPE_LABELS[conversationType] || conversationType;
  }

  private getBotCodeFromBotFullId(botFullId: string): string {
    const parts = botFullId.split('-');
    let botCode = parts[parts.length - 1];
    if (this.envs && this.envs.stages) {
      const env = this.envs.stages.filter(e => {
        const suffix = `_${e.systemName}`;
        return botCode.endsWith(suffix);
      });

      if (env.length > 0) {
        const suffix = `_${env[0].systemName}`;
        botCode = botCode.slice(0, botCode.length - suffix.length);
      }
    }
    return botCode;
  }

  private refreshContent() {
    this.updateBotUrl();
    this.loading = false;
  }

  private updateBotUrl() {
    if (this.corpId && this.selectedConversation?.hierarchyElements && this.selectedConversation?.hierarchyElements.length) {
      const lastHierarchy = this.selectedConversation.hierarchyElements[this.selectedConversation.hierarchyElements.length - 1];
      const botCode = this.getBotCodeFromBotFullId(this.selectedConversation.botId);
      this.botUrl = `/portal/corps/${this.corpId}/hierarchy-el/${lastHierarchy}/bots/${botCode}`;
    }
  }

  private extractCustomerContacts(): void {
    if (this.selectedConversation?.toEmailAddresses) {
      this.firstCustomerEmail = this.selectedConversation.toEmailAddresses.find(val => this.isEmail(val)) ?? null;
      this.firstCustomerPhone = this.selectedConversation.toEmailAddresses.find(val => isDigitsOnly(val)) ?? null;
      if (this.firstCustomerPhone) {
        this.firstCustomerPhone = formatDigitsOnlyToPhoneNumber(this.firstCustomerPhone);
      }
    }
  }

  private isEmail(value: string): boolean {
    return value.includes('@');
  }
}
